import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, ViewToggle, cn } from '@monorepo/common/components/ui';

/**
 * Published / Drafts — one destination, several states of the same thing.
 *
 * These were two sidebar entries, which made a post that is not published yet
 * look like a different kind of object living somewhere else. It is the same
 * object at an earlier point, so it belongs behind the same door.
 *
 * **Each tab is a real URL**, not component state: `/posts` and `/drafts` both
 * still work, a filtered timeline stays linkable (PostTimeline keeps its own
 * filters in the query string), and the back button does what it looks like it
 * does. That is also why switching navigates rather than swapping a render.
 *
 * **Scheduled sits between the two**, because that is where it is in a post's
 * life: written, given a time, then out. It was left off until it existed
 * rather than drawn greyed out — a disabled tab advertises a feature and then
 * refuses.
 */
const TABS = [
  { value: 'published', label: 'Published posts', text: 'Published', to: '/posts' },
  { value: 'scheduled', label: 'Scheduled posts', text: 'Scheduled', to: '/schedule' },
  { value: 'drafts', label: 'Drafts', text: 'Drafts', to: '/drafts' },
];

export const PostsTabs = ({ current, className }) => {
  const navigate = useNavigate();
  return (
    <ViewToggle
      className={className}
      label="Which posts"
      value={current}
      options={TABS}
      onChange={(value) => {
        const tab = TABS.find((t) => t.value === value);
        if (tab && value !== current) navigate(tab.to);
      }}
    />
  );
};

/**
 * The whole right-hand side of the Posts header: make one, or look at the
 * ones you have.
 *
 * **New post is always here**, before the toggle. It used to live only in the
 * Drafts page's non-empty branch, so switching to Drafts rendered it during
 * the loading pass and then took it away again when the list came back empty —
 * a button that flashes and vanishes reads as something going wrong. And
 * /posts never had it at all, which meant the action this app exists for was
 * missing from the screen listing everything the app has made.
 *
 * It is one component rather than two call sites so the two pages cannot
 * disagree about the order, the size, or whether it is there.
 *
 * Making a post is the primary action; the toggle is navigation. Primary
 * first, reading order left to right — and on a phone the label shortens,
 * because PageHeader is a single row there and "New post" plus two tab labels
 * plus the title does not fit at 390px.
 */
export const PostsActions = ({ current, className }) => (
  <div className={cn('flex shrink-0 items-center gap-2', className)}>
    <Button as={Link} to="/compose" variant="primary" size="sm">
      <span className="sm:hidden">New</span>
      <span className="hidden sm:inline">New post</span>
    </Button>
    <PostsTabs current={current} />
  </div>
);

export default PostsTabs;
